import { useSelected } from '@/hooks/useSelected/useSelected';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import useKeyPress from '../../../hooks/useKeyPress/useKeyPress';
import useOnClickOutside from '../../../hooks/useOnClickOutside/useOnClickOutside';
import FormField from '../FormField/FormField';
import MultiSelectCategoryItem from '../MultiSelect/MultiSelectCategoryItem/MultiSelectCategoryItem';
import MultiSelectItem from '../MultiSelect/MultiSelectItem/MultiSelectItem';
import styles from './FormFieldMultiSelect.module.scss';
import {
  FormFieldMultiSelectCategoryStatus,
  FormFieldMultiSelectOption,
  FormFieldMultiSelectProps,
} from './interfaces';

const hasEventTargetWithName = ($event: KeyboardEvent, name: string) =>
  $event.target && ($event.target as HTMLElement).id === name;

const PAGE_KEYS_STEP_LENGTH = 10;

const FormFieldMultiSelect = ({
  options,
  label,
  onChange,
  onBlur,
  errorMessage,
  name,
  selectedValues,
  initialSelectedValues,
  isBoxOnGradient = false,
  errorMessageHasRelativePosition,
}: FormFieldMultiSelectProps) => {
  const uniqueId = (name + (label ?? '')).toLocaleLowerCase();
  const listBoxId = `listbox-${uniqueId}`;
  const labelId = `label-${uniqueId}`;
  const ref = React.useRef<HTMLDivElement>(null);
  const optionListRef = React.useRef<HTMLUListElement>(null);
  const [showOptions, setShowOptions] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const { isSelected, numberOfSelected, setSelected, toggleSelected } =
    useSelected(selectedValues);

  const toggleShowOptions = React.useCallback(() => {
    setShowOptions((state) => !state);
  }, [setShowOptions]);

  const handleOnFocus = React.useCallback(
    () => toggleShowOptions(),
    [toggleShowOptions]
  );

  const getCategoryItems = React.useCallback(
    (category: string) => {
      return [...options]
        .filter((item) => item.category === category)
        .map(({ value }) => value);
    },
    [options]
  );

  const getInactiveCategoryItems = React.useCallback(
    (category: string) => {
      const categoryItems = getCategoryItems(category);

      const inactiveCategoryItems = categoryItems
        ? categoryItems.filter((item) => !isSelected(item))
        : [];

      return inactiveCategoryItems;
    },
    [getCategoryItems, isSelected]
  );

  const getCategoryStatus = React.useCallback(
    (category: string): FormFieldMultiSelectCategoryStatus => {
      const categoryItems = getCategoryItems(category);
      const inactiveCategoryItems = getInactiveCategoryItems(category) || [];

      const length = categoryItems?.length ?? 0;

      if (inactiveCategoryItems.length === 0) {
        return 'all';
      }
      if (inactiveCategoryItems.length === length) {
        return 'none';
      }
      return 'some';
    },
    [getCategoryItems, getInactiveCategoryItems]
  );

  const toggleCategory = React.useCallback(
    (category: string) => {
      const categoryItems = getCategoryItems(category) || [];
      const inactiveCategoryItems = getInactiveCategoryItems(category) || [];
      const categoryStatus = getCategoryStatus(category);

      setSelected((selected) => {
        if (categoryStatus === 'all') {
          const _selected = selected.filter(
            (element) => !categoryItems.includes(element)
          );

          onChangeRef?.current?.(_selected);

          return _selected;
        }
        const _selected = [...selected, ...inactiveCategoryItems];

        onChangeRef?.current?.(_selected);

        return _selected;
      });
    },
    [getCategoryItems, getCategoryStatus, getInactiveCategoryItems, setSelected]
  );

  const onPressArrow =
    (direction: 'up' | 'down') => ($event: KeyboardEvent) => {
      if (hasEventTargetWithName($event, name)) {
        $event.preventDefault();

        if (direction === 'up') {
          setActiveIndex((activeIndex - 1 + options.length) % options.length);
          return;
        }

        if (direction === 'down') {
          setActiveIndex((activeIndex + 1) % options.length);
          return;
        }
      }
    };

  const onPressEnter = React.useCallback(
    ($event: KeyboardEvent) => {
      if (hasEventTargetWithName($event, name)) {
        $event.preventDefault();

        if (options[activeIndex].type === 'category') {
          toggleCategory(options[activeIndex].value);
        } else {
          toggleSelected(options[activeIndex].value);
        }
      }
    },
    [activeIndex, name, options, toggleCategory, toggleSelected]
  );

  const onPressHome = ($event: KeyboardEvent) => {
    $event.preventDefault();
    setActiveIndex(0);
  };

  const onPressEnd = ($event: KeyboardEvent) => {
    $event.preventDefault();
    setActiveIndex(options.length - 1);
  };

  const onPressPageUp = ($event: KeyboardEvent) => {
    $event.preventDefault();

    if (activeIndex <= PAGE_KEYS_STEP_LENGTH) {
      setActiveIndex(0);
    } else {
      setActiveIndex(activeIndex - PAGE_KEYS_STEP_LENGTH);
    }
  };

  const onPressPageDown = ($event: KeyboardEvent) => {
    $event.preventDefault();

    if (options.length - activeIndex <= PAGE_KEYS_STEP_LENGTH) {
      setActiveIndex(options.length - 1);
    } else {
      setActiveIndex(activeIndex + PAGE_KEYS_STEP_LENGTH);
    }
  };
  const onPressTab = React.useCallback(
    ($event: KeyboardEvent) => {
      if (hasEventTargetWithName($event, name) && showOptions) {
        setShowOptions(false);
        if (ref.current) {
          ref.current.focus();
        }
        toggleShowOptions();
      }
    },
    [name, showOptions, toggleShowOptions]
  );

  useKeyPress('Tab', { onKeyDown: onPressTab });
  useKeyPress('Escape', { onKeyDown: onPressTab });
  useKeyPress('ArrowUp', { onKeyDown: onPressArrow('up') });
  useKeyPress('ArrowDown', { onKeyDown: onPressArrow('down') });
  useKeyPress('Enter', { onKeyDown: onPressEnter });
  useKeyPress(' ', { onKeyDown: onPressEnter });
  useKeyPress('Home', { onKeyDown: onPressHome });
  useKeyPress('End', { onKeyDown: onPressEnd });
  useKeyPress('PageUp', { onKeyDown: onPressPageUp });
  useKeyPress('PageDown', { onKeyDown: onPressPageDown });

  useOnClickOutside(ref, () => {
    if (showOptions) {
      setShowOptions(() => false);
    }
  });

  useEffect(() => {
    if (optionListRef?.current) {
      const currentRef: HTMLElement = optionListRef.current;
      currentRef.scrollTop =
        currentRef.children[0].clientHeight * (activeIndex - 1);
    }
  }, [activeIndex]);

  const handleOnClickOption = React.useCallback(
    ({ value }: FormFieldMultiSelectOption) => {
      setSelected((selected) => {
        if (selected.includes(value)) {
          const _selected = selected.filter((element) => element !== value);

          onChangeRef?.current?.(_selected);

          return _selected;
        }

        const _selected = [...selected, value];

        onChangeRef?.current?.(_selected);

        return _selected;
      });
    },
    [setSelected]
  );

  const setSelectedRef = React.useRef(setSelected);
  const [isInitialUpdated, setIsInitialUpdated] = React.useState(false);
  setSelectedRef.current = setSelected;

  React.useEffect(() => {
    if (
      initialSelectedValues &&
      initialSelectedValues?.length > 0 &&
      !isInitialUpdated
    ) {
      setSelectedRef.current(initialSelectedValues);
      setIsInitialUpdated(true);
    }
  }, [initialSelectedValues, isInitialUpdated]);

  return (
    <div
      ref={ref}
      tabIndex={-1}
      className={clsx(styles.base, { [styles.isOpen]: showOptions })}
      aria-owns={listBoxId}
      aria-haspopup="listbox"
      role="combobox"
      aria-expanded={showOptions}
      aria-activedescendant={`${listBoxId}-${activeIndex}`}
      aria-controls={listBoxId}
    >
      <FormField
        name={name}
        id={name}
        label={label}
        className={styles.formField}
        autoComplete="off"
        onFocus={handleOnFocus}
        onBlur={onBlur}
        errorMessage={errorMessage}
        suffixComponent={
          <span
            className={clsx(styles.placeholderNumber, {
              [styles.isActive]: numberOfSelected,
              [styles.withGradient]: isBoxOnGradient,
            })}
          >
            {numberOfSelected}
          </span>
        }
        icon={'action/chevron-down'}
        readOnly
        labelId={labelId}
        isBoxOnGradient={isBoxOnGradient}
        errorMessageHasRelativePosition={errorMessageHasRelativePosition}
      />

      {showOptions && (
        <ul
          ref={optionListRef}
          className={clsx(styles.list, {
            [styles.withScrollbar]: options.length > 3,
          })}
          aria-labelledby={labelId}
          role="listbox"
          id={listBoxId}
        >
          {options.map((option, index) => {
            if (option.type === 'category') {
              const categoryStatus = getCategoryStatus(option.value);

              return (
                <MultiSelectCategoryItem
                  key={'category-' + option.value}
                  listBoxId={`listbox-category${listBoxId}-${index}`}
                  onClick={() => toggleCategory(option.value)}
                  isFocus={index === activeIndex}
                  label={option.label}
                  status={categoryStatus}
                />
              );
            }

            return (
              <MultiSelectItem
                key={'item-' + option.value}
                listBoxId={`listbox-item${listBoxId}-${index}`}
                onClick={() => handleOnClickOption(option)}
                isFocus={index === activeIndex}
                item={option}
                isActive={isSelected(option.value)}
                indented={Boolean(option.category)}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FormFieldMultiSelect;
